/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { lazy, Suspense } from 'react';
import Loader from './loader';

const isTransientImportError = (err: unknown) => {
  const msg = (err as Error)?.message ?? '';

  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /NetworkError/i.test(msg) ||
    /is not a valid JavaScript MIME type/i.test(msg) ||
    /text\/html/i.test(msg) ||
    (err as any)?.name === 'ChunkLoadError' ||
    (err as any)?.code === 'ERR_NETWORK'
  );
};

const retryUntilSuccess = <T,>(fn: () => Promise<T>): Promise<T> => {
  return new Promise((resolve, reject) => {
    const attempt = () => {
      fn()
        .then(resolve)
        .catch((err) => {
          if (isTransientImportError(err)) {
            window.location.reload();
          } else {
            reject(err);
          }
        });
    };
    attempt();
  });
};

const Loadable =
  <P extends object>(Component: React.ComponentType<P>) =>
  (props: P) => (
    <Suspense
      fallback={
        <div
          style={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader />
        </div>
      }
    >
      <Component {...props} />
    </Suspense>
  );

export const LoadableComponent = <T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>
) => Loadable(lazy(() => retryUntilSuccess(importer)));

export default Loadable;
